import React, { useMemo } from 'react';
import { useLucideIcons } from '../../hooks/usePageAssets.js';
import { useGamification } from '../../hooks/useGamification.js';

const formatSyncDate = (value) => {
  if (!value) {
    return 'Sin sincronizar';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Sincronización reciente';
  }
  return parsed.toLocaleString('es-MX', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatRangeLabel = (start, end) => {
  if (!start || !end) {
    return '';
  }
  try {
    const startDate = new Date(`${start}T00:00:00`);
    const endDate = new Date(`${end}T00:00:00`);
    const fmt = (d) => d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
    return `${fmt(startDate)} – ${fmt(endDate)}`;
  } catch (error) {
    return `${start} – ${end}`;
  }
};

const EmptyLevel = () => ({
  nombre: 'Sin nivel',
  descripcion: 'Aún no hay datos suficientes para clasificar este rango.',
  color_hex: '#A0A0A0',
  text_color_hex: '#FFFFFF'
});

const STATUS_META = {
  hat_trick: { label: 'Hat Trick', tone: 'pinnacle' },
  goleador: { label: 'Goleador', tone: 'progress' },
  no_forecast: { label: 'Sin pronóstico', tone: 'absent' },
  na: { label: 'Gracia', tone: 'neutral' },
  closed: { label: 'Cerrado', tone: 'neutral' },
  future: { label: 'Pendiente', tone: 'pending' }
};

// Level → numeric rank as defined by the brief.
// vestidor=1 · calentando=2 · debutante=3 · jugador=4 · goleador=5 · hat_trick=6
const LEVEL_RANK = {
  en_el_vestidor: 1,
  calentando_motores: 2,
  debutante: 3,
  jugador_equipo: 4,
  goleador: 5,
  hat_trick: 6
};

const RANK_FALLBACK_LABEL = {
  1: 'En el vestidor',
  2: 'Calentando motores',
  3: 'Debutante',
  4: 'Jugador de equipo',
  5: 'Goleador',
  6: 'Hat Trick'
};

const formatWeekTick = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }).replace('.', '');
  } catch (_e) {
    return iso;
  }
};

const WEEKDAY_INITIALS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const WEEKDAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const DAYPART_COLORS = ['#C8102E', '#2F7D32', '#F2C94C', '#2D3A4B', '#78A22F', '#F2994A', '#12501A', '#586174'];
const UNMATCHED_DAYPART_COLOR = '#9CA3AF';

const escapeExcelCell = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

const downloadExcelTable = ({ filename, sheetTitle, headers, rows }) => {
  const tableRows = [
    `<tr>${headers.map((header) => `<th>${escapeExcelCell(header)}</th>`).join('')}</tr>`,
    ...rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeExcelCell(cell)}</td>`).join('')}</tr>`)
  ];
  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      body { font-family: Arial, sans-serif; }
      table { border-collapse: collapse; }
      th { background: #C8102E; color: #fff; font-weight: 700; }
      th, td { border: 1px solid #d9d9d9; padding: 6px 8px; font-size: 12px; }
    </style>
  </head>
  <body>
    <h1>${escapeExcelCell(sheetTitle)}</h1>
    <table>${tableRows.join('')}</table>
  </body>
</html>`;
  const blob = new Blob([`\ufeff${html}`], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const statusLabel = (status) => STATUS_META[status]?.label || status || '';

const downloadTrajectorySupport = (weeks = []) => {
  const rows = [];
  weeks.forEach((week) => {
    const levelName = week?.score?.level?.nombre || week?.score?.level_slug || 'Sin nivel';
    const progress = Number.isFinite(Number(week?.score?.progress_pct)) ? `${Number(week.score.progress_pct).toFixed(1)}%` : '';
    (week.daily_support || []).forEach((day) => {
      rows.push([
        week.week_start,
        week.week_end,
        levelName,
        progress,
        day.date,
        WEEKDAY_NAMES[(Number(day.weekday) || 1) - 1] || '',
        statusLabel(day.status),
        day.forecast_events || 0,
        day.operated ? 'Sí' : 'No',
        day.effective ? 'Sí' : 'No',
        day.in_grace ? 'Sí' : 'No',
        day.operated_restaurants ?? '',
        day.effective_restaurants ?? '',
        day.in_grace_restaurants ?? ''
      ]);
    });
  });
  downloadExcelTable({
    filename: `soporte_trayectoria_${new Date().toISOString().slice(0, 10)}.xls`,
    sheetTitle: 'Soporte trayectoria semanal',
    headers: [
      'Semana inicio',
      'Semana fin',
      'Nivel semana',
      'Cumplimiento',
      'Fecha',
      'Día',
      'Estado',
      'Pronósticos',
      'Operado',
      'Efectivo',
      'Gracia',
      'Restaurantes operados',
      'Restaurantes efectivos',
      'Restaurantes en gracia'
    ],
    rows
  });
};

const downloadDaypartSupport = (data) => {
  const rows = [];
  (data?.weeks || []).forEach((week) => {
    (week.details || []).forEach((detail) => {
      const percentage = Number(week.total) > 0 ? (Number(detail.count || 0) / Number(week.total)) * 100 : 0;
      rows.push([
        week.week_start,
        week.week_end,
        detail.forecast_date,
        formatDaypartHour(detail.forecast_hour),
        detail.daypart_nombre || 'Sin daypart',
        detail.count || 0,
        `${percentage.toFixed(1)}%`
      ]);
    });
  });
  downloadExcelTable({
    filename: `pronosticos_por_daypart_${new Date().toISOString().slice(0, 10)}.xls`,
    sheetTitle: 'Pronósticos por daypart',
    headers: ['Semana inicio', 'Semana fin', 'Fecha', 'Hora del pronóstico', 'Daypart', 'Cantidad', 'Porcentaje semanal'],
    rows
  });
};

const LevelLogo = ({ level, size = 96, className = '' }) => {
  const palette = level || EmptyLevel();
  const showImage = Boolean(palette.image_url);
  return (
    <div
      className={`level-logo ${className}`.trim()}
      style={{
        '--level-color': palette.color_hex || '#C8102E',
        '--level-text': palette.text_color_hex || '#FFFFFF',
        width: `${size}px`,
        height: `${size}px`
      }}
      aria-label={palette.nombre}
    >
      {showImage ? (
        <img src={palette.image_url} alt={palette.nombre} className="level-logo__image" />
      ) : (
        <span className="level-logo__monogram">{(palette.nombre || '·').charAt(0)}</span>
      )}
    </div>
  );
};

const LevelChip = ({ level }) => {
  const palette = level || EmptyLevel();
  return (
    <span
      className="gamification-level-chip"
      style={{
        '--level-color': palette.color_hex || '#C8102E',
        '--level-text': palette.text_color_hex || '#FFFFFF'
      }}
    >
      {palette.image_url ? (
        <img src={palette.image_url} alt="" className="gamification-level-chip__image" />
      ) : (
        <span className="gamification-level-chip__dot" />
      )}
      <span>{palette.nombre}</span>
    </span>
  );
};

const RankingList = ({ title, icon, items }) => (
  <div className="gamification-ranking-panel">
    <div className="gamification-ranking-title">
      <i data-lucide={icon} />
      <span>{title}</span>
    </div>
    <div className="gamification-ranking-list">
      {items.length ? items.map((item, index) => (
        <div className="gamification-ranking-row" key={`${title}-${item.restaurant_id || item.restaurant_name}-${index}`}>
          <span className="ranking-position">{String(index + 1).padStart(2, '0')}</span>
          <div className="ranking-restaurant">
            <strong>{item.restaurant_name || 'Restaurante'}</strong>
            <span>{item.days_with_forecast}/{item.days_effective || 0} días pronosticados</span>
          </div>
          <LevelChip level={item.level} />
        </div>
      )) : (
        <div className="gamification-empty-row">Sin datos para este rango.</div>
      )}
    </div>
  </div>
);

const formatDaypartRange = (segment) => {
  if (!segment || segment.key === 'unmatched') return null;
  const start = segment.hora_inicio;
  const end = segment.hora_fin;
  if (start === null || start === undefined || end === null || end === undefined) {
    return null;
  }
  return `${formatDaypartHour(start)}–${formatDaypartHour(end)}`;
};

const WeekTrack = ({ data }) => {
  const todayTs = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t.getTime();
  }, []);
  const isMondayToday = new Date(todayTs).getDay() === 1;

  const weekData = useMemo(() => {
    if (!data?.start) {
      return [];
    }
    const monday = new Date(`${data.start}T00:00:00`);
    return Array.from({ length: 7 }).map((_, idx) => {
      const cellDate = new Date(monday);
      cellDate.setDate(monday.getDate() + idx);
      cellDate.setHours(0, 0, 0, 0);
      const iso = `${cellDate.getFullYear()}-${String(cellDate.getMonth() + 1).padStart(2, '0')}-${String(cellDate.getDate()).padStart(2, '0')}`;
      const found = (data.days || []).find((d) => d.date === iso);
      const isFuture = cellDate.getTime() > todayTs;
      const isToday = cellDate.getTime() === todayTs;
      // Today is not a complete day yet — show it as pending (same treatment
      // as future days) so a forecast generated this morning doesn't appear
      // classified as Goleador/Hat Trick while the day is still running.
      const isPending = isFuture || isToday;
      const status = isPending ? 'future' : (found?.status || 'closed');
      const dayparts = Array.isArray(found?.dayparts) ? found.dayparts : [];
      return {
        iso,
        weekday: WEEKDAY_INITIALS[idx],
        weekdayName: WEEKDAY_NAMES[idx],
        date: cellDate,
        isToday,
        isFuture,
        isPending,
        forecastEvents: Number(found?.forecast_events || 0),
        effective: Boolean(found?.effective),
        operated: Boolean(found?.operated),
        inGrace: Boolean(found?.in_grace),
        status,
        meta: STATUS_META[status] || STATUS_META.future,
        dayparts
      };
    });
  }, [data, todayTs]);

  // Metrics ignore today entirely — same reason as above. A day only counts
  // toward "días jugados / días con pronóstico / hat tricks" once it ends.
  const measuredDays = weekData.filter((d) => !d.isPending && (d.operated || d.inGrace || d.forecastEvents > 0));
  const goalDays = weekData.filter((d) => d.effective && !d.isPending);
  const goalsAchieved = goalDays.filter((d) => d.forecastEvents >= 1).length;
  const hatTrickDays = goalDays.filter((d) => d.forecastEvents >= 3).length;
  const totalForecasts = weekData.reduce((acc, d) => acc + d.forecastEvents, 0);

  // Cross-week daypart totals to power the legend at the bottom.
  const daypartTotals = useMemo(() => {
    const map = new Map();
    weekData.forEach((day) => {
      day.dayparts.forEach((seg) => {
        if (!seg || seg.key === 'unmatched') return;
        const existing = map.get(seg.key);
        if (existing) {
          existing.count += Number(seg.count) || 0;
        } else {
          map.set(seg.key, {
            key: seg.key,
            nombre: seg.nombre,
            hora_inicio: seg.hora_inicio,
            hora_fin: seg.hora_fin,
            color_index: Number.isFinite(Number(seg.color_index)) ? Number(seg.color_index) : 0,
            count: Number(seg.count) || 0,
          });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => {
      const sa = a.hora_inicio ?? 99;
      const sb = b.hora_inicio ?? 99;
      return sa - sb;
    });
  }, [weekData]);

  return (
    <div className="week-track">
      <div className="week-track__header">
        <div>
          <span className="section-eyebrow">Semana en juego</span>
          <h3>Cómo va esta semana</h3>
          <p className="week-track__subtitle">
            <strong>{totalForecasts}</strong> pronóstico{totalForecasts === 1 ? '' : 's'} esta semana
            <span aria-hidden="true"> · </span>
            {measuredDays.length} día{measuredDays.length === 1 ? '' : 's'} jugado{measuredDays.length === 1 ? '' : 's'}
          </p>
        </div>
        <span className="week-track__range">{formatRangeLabel(data?.start, data?.end)}</span>
      </div>

      {isMondayToday ? (
        <div className="week-track__legend" role="status">
          <span className="week-track__legend-icon"><i data-lucide="info" /></span>
          <div>
            <strong>El partido apenas comienza.</strong>
            <span>Los lunes aún no se muestran resultados; el día se cuenta cuando termine la jornada.</span>
          </div>
        </div>
      ) : null}

      <div className="week-track__grid" role="list">
        {weekData.map((day) => {
          const operatedLabel = day.isPending
            ? 'Pendiente'
            : day.operated || day.forecastEvents > 0
              ? 'Operado'
              : day.inGrace
                ? 'Gracia'
                : 'No operado';
          const operatedKey = day.isPending
            ? 'pending'
            : day.operated || day.forecastEvents > 0
              ? 'operated'
              : day.inGrace
                ? 'grace'
                : 'closed';
          const statusIcon = (() => {
            if (day.isPending) return 'clock';
            if (day.operated || day.forecastEvents > 0) return 'check-circle-2';
            if (day.inGrace) return 'shield';
            return 'minus-circle';
          })();
          const visibleSegments = day.dayparts.slice(0, 3);
          const remainingSegments = day.dayparts.length - visibleSegments.length;
          return (
            <div
              key={day.iso}
              role="listitem"
              className={`week-cell week-cell--${day.meta.tone} ${day.isToday ? 'is-today' : ''} ${day.isFuture ? 'is-future' : ''}`}
              title={`${day.weekdayName} ${day.date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} · ${day.meta.label}`}
            >
              <header className="week-cell__head">
                <div className="week-cell__heading">
                  <span className="week-cell__weekday">{day.weekdayName}</span>
                  <span className="week-cell__date">{day.date.getDate()}</span>
                </div>
                {day.isToday ? <span className="week-cell__live">Hoy</span> : null}
              </header>

              <div className={`week-cell__operated week-cell__operated--${operatedKey}`}>
                <i data-lucide={statusIcon} />
                <span>{operatedLabel}</span>
              </div>

              <div className="week-cell__forecasts" aria-label={`${day.forecastEvents} pronósticos`}>
                <strong>{day.forecastEvents}</strong>
                <span>pronóstico{day.forecastEvents === 1 ? '' : 's'}</span>
              </div>

              {day.forecastEvents > 0 && day.dayparts.length ? (
                <ul className="week-cell__dayparts" aria-label="Daypart de cada pronóstico">
                  {visibleSegments.map((seg) => {
                    const colorIndex = Number.isFinite(Number(seg.color_index)) ? Number(seg.color_index) : 0;
                    const color = seg.key === 'unmatched' ? UNMATCHED_DAYPART_COLOR : DAYPART_COLORS[colorIndex % DAYPART_COLORS.length];
                    const range = formatDaypartRange(seg);
                    return (
                      <li className="week-cell__daypart" key={`${day.iso}-${seg.key}`}>
                        <span className="week-cell__daypart-dot" style={{ background: color }} />
                        <span className="week-cell__daypart-name" title={seg.nombre}>{seg.nombre}</span>
                        <span className="week-cell__daypart-count">×{seg.count}</span>
                        {range ? <span className="week-cell__daypart-range">{range}</span> : null}
                      </li>
                    );
                  })}
                  {remainingSegments > 0 ? (
                    <li className="week-cell__daypart week-cell__daypart--more">
                      <span>+{remainingSegments} daypart{remainingSegments === 1 ? '' : 's'}</span>
                    </li>
                  ) : null}
                </ul>
              ) : (
                <div className="week-cell__no-dayparts">
                  {day.isPending ? 'Aún sin jugar' : day.forecastEvents === 0 ? 'Sin pronósticos' : 'Sin daypart'}
                </div>
              )}

              <span className="week-cell__pill">{day.meta.label}</span>
            </div>
          );
        })}
      </div>

      {daypartTotals.length ? (
        <div className="week-track__daypart-legend" aria-label="Distribución de pronósticos por daypart">
          {daypartTotals.map((seg) => {
            const color = DAYPART_COLORS[(seg.color_index || 0) % DAYPART_COLORS.length];
            const range = formatDaypartRange(seg);
            return (
              <span className="week-track__daypart-chip" key={seg.key}>
                <span className="week-track__daypart-chip-dot" style={{ background: color }} />
                <strong>{seg.nombre}</strong>
                {range ? <em>{range}</em> : null}
                <span className="week-track__daypart-chip-count">{seg.count}</span>
              </span>
            );
          })}
        </div>
      ) : null}

      <div className="week-track__metrics">
        <div>
          <strong>{goalsAchieved}<span>/{goalDays.length || '—'}</span></strong>
          <span>Días con pronóstico</span>
        </div>
        <div>
          <strong>{hatTrickDays}</strong>
          <span>Hat Tricks</span>
        </div>
        <div>
          <strong>{measuredDays.length}</strong>
          <span>Días jugados</span>
        </div>
        <div>
          <strong>{totalForecasts}</strong>
          <span>Pronósticos generados</span>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// LevelLegend — horizontal Cloudflare-image-driven legend used by both charts.
// ---------------------------------------------------------------------------
const LevelLegend = ({ levels, activeRank }) => {
  // Order ascending by rank (1 → 6) so reading flows like a progression bar.
  const ordered = useMemo(() => {
    return [...(levels || [])]
      .filter((l) => l && (l.slug in LEVEL_RANK))
      .sort((a, b) => (LEVEL_RANK[a.slug] || 0) - (LEVEL_RANK[b.slug] || 0));
  }, [levels]);

  if (!ordered.length) return null;

  return (
    <div className="level-trajectory__legend" role="list" aria-label="Niveles de juego">
      {ordered.map((level) => {
        const rank = LEVEL_RANK[level.slug];
        const isActive = activeRank === rank;
        const fullName = level.nombre || RANK_FALLBACK_LABEL[rank];
        return (
          <div
            key={level.slug}
            role="listitem"
            className={`legend-token ${isActive ? 'legend-token--active' : ''}`}
            style={{ '--legend-color': level.color_hex || '#C8102E' }}
          >
            <span className="legend-token__avatar">
              {level.image_url ? (
                <img src={level.image_url} alt="" loading="lazy" />
              ) : (
                <span className="legend-token__monogram">{(fullName || '·').charAt(0)}</span>
              )}
              <span className="legend-token__rank" aria-hidden="true">{String(rank).padStart(2, '0')}</span>
            </span>
            <span className="legend-token__copy">
              <strong title={fullName}>{fullName}</strong>
              <em>Nivel {rank}</em>
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// LevelLineChart — single restaurant trajectory across the selected weeks.
// Y axis: rank 1 → 6, X axis: weeks (Monday markers).
// ---------------------------------------------------------------------------
const LevelLineChart = ({ weeks, levels, lastCompleteIndex, summaryLevel }) => {
  const points = useMemo(() => {
    return (weeks || []).map((entry, idx) => {
      const slug = entry?.score?.level_slug;
      const rank = slug ? (LEVEL_RANK[slug] || null) : null;
      const level = entry?.score?.level || (slug ? levels.find((l) => l.slug === slug) : null);
      return {
        idx,
        weekStart: entry?.week_start,
        weekEnd: entry?.week_end,
        rank,
        slug,
        level,
        progress: Number(entry?.score?.progress_pct) || 0,
        hasData: rank !== null
      };
    });
  }, [weeks, levels]);

  // Chart geometry — viewBox is fixed; CSS scales the SVG fluidly.
  // PAD_L houses the y-axis avatars + rank chip.
  // PAD_T is generous to host the "ÚLTIMA" flag pill above the highlight column.
  // PAD_R is tight because we no longer draw the right-side level labels.
  const VB_W = 900;
  const VB_H = 380;
  const PAD_L = 78;
  const PAD_R = 28;
  const PAD_T = 52;
  const PAD_B = 50;
  const innerW = VB_W - PAD_L - PAD_R;
  const innerH = VB_H - PAD_T - PAD_B;

  const xFor = (idx) => {
    if (points.length <= 1) return PAD_L + innerW / 2;
    return PAD_L + (innerW * idx) / (points.length - 1);
  };
  const yFor = (rank) => PAD_T + innerH - ((rank - 1) / 5) * innerH;

  // Build a smooth Catmull-Rom-ish polyline through points that have data.
  // Gaps are skipped (drawn as dashed segment).
  const segments = useMemo(() => {
    const out = [];
    let current = [];
    points.forEach((p) => {
      if (p.hasData) {
        current.push(p);
      } else if (current.length) {
        out.push(current);
        current = [];
      }
    });
    if (current.length) out.push(current);
    return out;
  }, [points]);

  const buildPath = (seg) => {
    if (!seg.length) return '';
    if (seg.length === 1) {
      const x = xFor(seg[0].idx);
      const y = yFor(seg[0].rank);
      return `M ${x} ${y}`;
    }
    // Smooth cardinal-ish spline using midpoint quadratic curves.
    let d = `M ${xFor(seg[0].idx)} ${yFor(seg[0].rank)}`;
    for (let i = 1; i < seg.length; i++) {
      const prev = seg[i - 1];
      const cur = seg[i];
      const x1 = xFor(prev.idx);
      const y1 = yFor(prev.rank);
      const x2 = xFor(cur.idx);
      const y2 = yFor(cur.rank);
      const cx = (x1 + x2) / 2;
      d += ` C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`;
    }
    return d;
  };

  const lastValidPoint = useMemo(() => {
    for (let i = points.length - 1; i >= 0; i -= 1) {
      if (points[i].hasData) return points[i];
    }
    return null;
  }, [points]);

  const lastCompletePoint = points[lastCompleteIndex] || lastValidPoint;

  return (
    <div className="level-trajectory__chart-frame">
      <svg
        className="level-line-chart"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        role="img"
        aria-label="Trayectoria de nivel semanal"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="line-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C8102E" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#7C1525" stopOpacity="0.95" />
          </linearGradient>
          <linearGradient id="line-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C8102E" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#C8102E" stopOpacity="0" />
          </linearGradient>
          <filter id="line-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>

        {/* Y-axis: pitch line + level avatar + rank chip on the left gutter */}
        {[1, 2, 3, 4, 5, 6].map((rank) => {
          const lvl = (levels || []).find((l) => LEVEL_RANK[l.slug] === rank);
          const y = yFor(rank);
          const avatarSize = 26;
          const avatarX = 6;
          const chipCx = avatarX + avatarSize + 14;
          return (
            <g key={`grid-${rank}`} className="level-line-chart__row">
              <line
                x1={PAD_L}
                x2={VB_W - PAD_R}
                y1={y}
                y2={y}
                className="level-line-chart__gridline"
              />
              {lvl?.image_url ? (
                <image
                  href={lvl.image_url}
                  x={avatarX}
                  y={y - avatarSize / 2}
                  width={avatarSize}
                  height={avatarSize}
                  preserveAspectRatio="xMidYMid meet"
                >
                  <title>{lvl.nombre || RANK_FALLBACK_LABEL[rank]}</title>
                </image>
              ) : (
                <circle
                  cx={avatarX + avatarSize / 2}
                  cy={y}
                  r={avatarSize / 2 - 1}
                  fill={lvl?.color_hex || '#C8102E'}
                  opacity="0.85"
                />
              )}
              <circle
                cx={chipCx}
                cy={y}
                r={10}
                className="level-line-chart__rank-chip"
                style={{ '--chip-color': lvl?.color_hex || '#C8102E' }}
              />
              <text
                x={chipCx}
                y={y + 4}
                className="level-line-chart__rank-num"
                textAnchor="middle"
              >
                {rank}
              </text>
            </g>
          );
        })}

        {/* Last-complete-week — solid tinted column + flag pill at the top */}
        {lastCompletePoint ? (() => {
          const cx = xFor(lastCompletePoint.idx);
          const colW = 38;
          // Clamp the flag pill horizontally so it never overflows the viewBox.
          const pillW = 76;
          const minPillCx = PAD_L + pillW / 2 + 2;
          const maxPillCx = VB_W - PAD_R - pillW / 2 - 2;
          const pillCx = Math.min(Math.max(cx, minPillCx), maxPillCx);
          return (
            <g className="level-line-chart__last-column">
              <rect
                x={cx - colW / 2}
                y={PAD_T - 4}
                width={colW}
                height={innerH + 12}
                rx={8}
                className="level-line-chart__last-rect"
              />
              {/* Flag pill */}
              <g className="level-line-chart__last-flag">
                <rect
                  x={pillCx - pillW / 2}
                  y={10}
                  width={pillW}
                  height={20}
                  rx={10}
                  className="level-line-chart__last-pill"
                />
                <text
                  x={pillCx}
                  y={24}
                  textAnchor="middle"
                  className="level-line-chart__last-pill-text"
                >
                  ÚLTIMA
                </text>
                {/* Tether from pill toward the column when offset */}
                {pillCx !== cx ? (
                  <line
                    x1={pillCx}
                    y1={30}
                    x2={cx}
                    y2={PAD_T - 4}
                    className="level-line-chart__last-tether"
                  />
                ) : (
                  <path
                    d={`M ${cx - 6} 30 L ${cx + 6} 30 L ${cx} 38 Z`}
                    className="level-line-chart__last-arrow"
                  />
                )}
              </g>
            </g>
          );
        })() : null}

        {/* Filled area under the line (solid segments only) */}
        {segments.map((seg, sIdx) => {
          if (seg.length < 2) return null;
          const path = buildPath(seg);
          const lastX = xFor(seg[seg.length - 1].idx);
          const firstX = xFor(seg[0].idx);
          const baseY = PAD_T + innerH;
          return (
            <path
              key={`area-${sIdx}`}
              d={`${path} L ${lastX} ${baseY} L ${firstX} ${baseY} Z`}
              fill="url(#line-fill)"
              className="level-line-chart__area"
            />
          );
        })}

        {/* Glow + main stroke per segment */}
        {segments.map((seg, sIdx) => {
          const path = buildPath(seg);
          return (
            <g key={`seg-${sIdx}`}>
              <path
                d={path}
                stroke="url(#line-grad)"
                strokeWidth="6"
                fill="none"
                filter="url(#line-glow)"
                opacity="0.45"
              />
              <path
                d={path}
                stroke="url(#line-grad)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                className="level-line-chart__stroke"
              />
            </g>
          );
        })}

        {/* Connector dashes between gap segments to imply continuity */}
        {segments.length > 1 ? segments.map((seg, sIdx) => {
          if (sIdx === segments.length - 1) return null;
          const next = segments[sIdx + 1];
          const a = seg[seg.length - 1];
          const b = next[0];
          return (
            <line
              key={`gap-${sIdx}`}
              x1={xFor(a.idx)}
              y1={yFor(a.rank)}
              x2={xFor(b.idx)}
              y2={yFor(b.rank)}
              stroke="#C8102E"
              strokeWidth="1.5"
              strokeDasharray="4 5"
              opacity="0.35"
            />
          );
        }) : null}

        {/* Data dots */}
        {points.map((p) => {
          if (!p.hasData) return null;
          const isLast = lastCompletePoint && p.idx === lastCompletePoint.idx;
          return (
            <g key={`dot-${p.idx}`} className={`level-dot ${isLast ? 'level-dot--last' : ''}`}>
              <circle
                cx={xFor(p.idx)}
                cy={yFor(p.rank)}
                r={isLast ? 9 : 5}
                fill="#FFFFFF"
                stroke={p.level?.color_hex || '#C8102E'}
                strokeWidth={isLast ? 3.5 : 2.25}
              />
              {isLast ? (
                <circle
                  cx={xFor(p.idx)}
                  cy={yFor(p.rank)}
                  r={14}
                  fill="none"
                  stroke={p.level?.color_hex || '#C8102E'}
                  strokeWidth="1.25"
                  opacity="0.4"
                  className="level-dot__halo"
                />
              ) : null}
            </g>
          );
        })}

        {/* X-axis week labels — anchor edges to keep them inside the viewBox */}
        {points.map((p) => {
          const isLast = lastCompletePoint && p.idx === lastCompletePoint.idx;
          let anchor = 'middle';
          if (p.idx === 0) anchor = 'start';
          else if (p.idx === points.length - 1) anchor = 'end';
          return (
            <text
              key={`xlbl-${p.idx}`}
              x={xFor(p.idx)}
              y={VB_H - 22}
              textAnchor={anchor}
              className={`level-line-chart__xtick ${isLast ? 'is-last' : ''}`}
            >
              {formatWeekTick(p.weekStart)}
            </text>
          );
        })}

        {/* Floating level logo above the last-complete-week dot.
            The OUTER <g> owns the SVG transform (positioning); the INNER
            <g> owns the CSS transform (drop animation). Keeping them on
            different elements prevents the CSS transform from overriding
            the SVG one and snapping the pin to the chart origin. */}
        {lastCompletePoint && lastCompletePoint.level?.image_url ? (
          <g
            transform={`translate(${xFor(lastCompletePoint.idx)}, ${yFor(lastCompletePoint.rank) - 50})`}
          >
            <g className="level-line-chart__pin">
              <line
                x1="0"
                y1="34"
                x2="0"
                y2="44"
                stroke={lastCompletePoint.level?.color_hex || '#C8102E'}
                strokeWidth="1.5"
                opacity="0.55"
              />
              <circle r="24" fill="#FFFFFF" stroke={lastCompletePoint.level?.color_hex || '#C8102E'} strokeWidth="2.5" />
              <circle r="27" fill="none" stroke={lastCompletePoint.level?.color_hex || '#C8102E'} strokeWidth="1" opacity="0.18" />
              <image
                href={lastCompletePoint.level.image_url}
                x="-20"
                y="-20"
                width="40"
                height="40"
                preserveAspectRatio="xMidYMid meet"
              />
            </g>
          </g>
        ) : null}
      </svg>
    </div>
  );
};

// ---------------------------------------------------------------------------
// LevelStackedBars — multi-restaurant 100% breakdown per week.
// ---------------------------------------------------------------------------
const LevelStackedBars = ({ weeks, levels, lastCompleteIndex }) => {
  const orderedLevels = useMemo(() => {
    return [...(levels || [])]
      .filter((l) => l && l.slug in LEVEL_RANK)
      .sort((a, b) => (LEVEL_RANK[a.slug] || 0) - (LEVEL_RANK[b.slug] || 0));
  }, [levels]);

  const VB_W = 900;
  const VB_H = 380;
  const PAD_L = 60;
  const PAD_R = 24;
  const PAD_T = 56; // room for the ÚLTIMA flag pill above the highlighted column
  const PAD_B = 50;
  const innerW = VB_W - PAD_L - PAD_R;
  const innerH = VB_H - PAD_T - PAD_B;

  const totalBars = (weeks || []).length || 1;
  const barGap = 12;
  const barW = Math.max(20, (innerW - barGap * (totalBars - 1)) / totalBars);

  return (
    <div className="level-trajectory__chart-frame">
      <svg
        className="level-stacked-chart"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        role="img"
        aria-label="Distribución de niveles por semana"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Horizontal pitch grid every 25% */}
        {[0, 0.25, 0.5, 0.75, 1].map((frac, i) => {
          const y = PAD_T + innerH * (1 - frac);
          return (
            <g key={`hg-${i}`}>
              <line
                x1={PAD_L}
                x2={VB_W - PAD_R}
                y1={y}
                y2={y}
                className="level-stacked-chart__gridline"
              />
              <text x={PAD_L - 12} y={y + 4} textAnchor="end" className="level-stacked-chart__yticks">
                {Math.round(frac * 100)}%
              </text>
            </g>
          );
        })}

        {(weeks || []).map((entry, idx) => {
          const total = Number(entry?.restaurants_with_level) || 0;
          const distribution = entry?.level_distribution || {};
          const baseX = PAD_L + idx * (barW + barGap);
          let cursorY = PAD_T + innerH; // Start at the bottom and stack upward.
          const isLast = idx === lastCompleteIndex;

          // Stack from rank 1 (vestidor) at the bottom → rank 6 (hat trick) at the top.
          const segs = orderedLevels
            .map((lvl) => {
              const count = Number(distribution[lvl.slug]) || 0;
              if (!count || !total) return null;
              const pct = (count / total) * 100;
              const segH = (pct / 100) * innerH;
              const y = cursorY - segH;
              cursorY = y;
              return { level: lvl, count, pct, y, h: segH };
            })
            .filter(Boolean);

          return (
            <g key={`bar-${idx}`} className={`stacked-bar ${isLast ? 'stacked-bar--last' : ''}`}>
              {/* Empty slot (when no data) */}
              {!segs.length ? (
                <rect
                  x={baseX}
                  y={PAD_T + innerH - 4}
                  width={barW}
                  height={4}
                  className="level-stacked-chart__empty"
                  rx="2"
                />
              ) : null}

              {/* Highlight backplate for the last complete week */}
              {isLast ? (
                <rect
                  x={baseX - 6}
                  y={PAD_T - 8}
                  width={barW + 12}
                  height={innerH + 16}
                  className="level-stacked-chart__highlight"
                  rx="10"
                />
              ) : null}

              {segs.map((seg, sIdx) => {
                const showLabel = seg.pct >= 7;
                const radius = sIdx === segs.length - 1 ? 6 : 0;
                return (
                  <g key={`seg-${idx}-${seg.level.slug}`}>
                    <rect
                      x={baseX}
                      y={seg.y}
                      width={barW}
                      height={Math.max(seg.h, 1)}
                      fill={seg.level.color_hex || '#C8102E'}
                      className="level-stacked-chart__seg"
                      style={{
                        '--seg-color': seg.level.color_hex || '#C8102E',
                        animationDelay: `${idx * 60 + sIdx * 35}ms`
                      }}
                    >
                      <title>
                        {`${seg.level.nombre} · ${seg.count}/${total} · ${seg.pct.toFixed(1)}%`}
                      </title>
                    </rect>
                    {showLabel ? (
                      <text
                        x={baseX + barW / 2}
                        y={seg.y + seg.h / 2 + 4}
                        textAnchor="middle"
                        className="level-stacked-chart__seg-label"
                        fill={seg.level.text_color_hex || '#FFFFFF'}
                      >
                        {seg.pct >= 99.5 ? '100' : seg.pct.toFixed(seg.pct < 10 ? 1 : 0)}%
                      </text>
                    ) : null}
                  </g>
                );
              })}

              {/* Top cap label = restaurant total */}
              <text
                x={baseX + barW / 2}
                y={PAD_T - 12}
                textAnchor="middle"
                className={`level-stacked-chart__total ${isLast ? 'is-last' : ''}`}
              >
                {total > 0 ? `${total} rest.` : '—'}
              </text>

              {/* X axis tick — anchor edges so the date never gets clipped */}
              <text
                x={baseX + barW / 2}
                y={VB_H - 22}
                textAnchor={
                  idx === 0 ? 'start' : idx === (weeks || []).length - 1 ? 'end' : 'middle'
                }
                className={`level-stacked-chart__xtick ${isLast ? 'is-last' : ''}`}
              >
                {formatWeekTick(entry.week_start)}
              </text>
            </g>
          );
        })}

        {/* "ÚLTIMA" flag pill rendered last so it always paints above the bars.
            Clamped within the viewBox so it never gets clipped at the edges. */}
        {lastCompleteIndex >= 0 ? (() => {
          const baseX = PAD_L + lastCompleteIndex * (barW + barGap);
          const cx = baseX + barW / 2;
          const pillW = 76;
          const minPillCx = PAD_L + pillW / 2 + 2;
          const maxPillCx = VB_W - PAD_R - pillW / 2 - 2;
          const pillCx = Math.min(Math.max(cx, minPillCx), maxPillCx);
          return (
            <g className="level-stacked-chart__last-flag">
              <rect
                x={pillCx - pillW / 2}
                y={10}
                width={pillW}
                height={20}
                rx={10}
                className="level-stacked-chart__last-pill"
              />
              <text
                x={pillCx}
                y={24}
                textAnchor="middle"
                className="level-stacked-chart__last-pill-text"
              >
                ÚLTIMA
              </text>
              {pillCx !== cx ? (
                <line
                  x1={pillCx}
                  y1={30}
                  x2={cx}
                  y2={PAD_T - 8}
                  className="level-stacked-chart__last-tether"
                />
              ) : (
                <path
                  d={`M ${cx - 6} 30 L ${cx + 6} 30 L ${cx} 38 Z`}
                  className="level-stacked-chart__last-arrow"
                />
              )}
            </g>
          );
        })() : null}
      </svg>
    </div>
  );
};

// ---------------------------------------------------------------------------
// LevelTrajectory — wrapper that picks line vs stacked bars and shows legend.
// ---------------------------------------------------------------------------
const LevelTrajectory = ({ weeks, levels, isSingleRestaurant, summaryLevel }) => {
  // The list comes back oldest → newest. The "last complete week" is the
  // most recent entry that ended before today (i.e., the last index).
  const lastCompleteIndex = Array.isArray(weeks) && weeks.length ? weeks.length - 1 : -1;
  const activeRank = summaryLevel ? LEVEL_RANK[summaryLevel.slug] : null;

  if (!Array.isArray(weeks) || !weeks.length) {
    return (
      <div className="level-trajectory level-trajectory--empty">
        <span className="section-eyebrow">Trayectoria</span>
        <p>Aún no hay semanas con datos suficientes para graficar.</p>
      </div>
    );
  }

  return (
    <div className="level-trajectory">
      <header className="level-trajectory__header">
        <div>
          <span className="section-eyebrow">
            {isSingleRestaurant ? 'Trayectoria semanal' : 'Composición semanal'}
          </span>
          <h4 className="level-trajectory__title">
            {isSingleRestaurant
              ? `Niveles alcanzados en las últimas ${weeks.length} semanas`
              : `Cómo se reparten los ${weeks.length > 1 ? 'restaurantes' : 'restaurantes'} por nivel`}
          </h4>
        </div>
        <div className="level-trajectory__actions">
          <div className="level-trajectory__legend-marker">
            <span className="legend-marker__dot" />
            <span>Última semana completa</span>
          </div>
          <button
            className="gamification-export-btn"
            type="button"
            onClick={() => downloadTrajectorySupport(weeks)}
          >
            <i data-lucide="download" />
            <span>Excel</span>
          </button>
        </div>
      </header>

      {isSingleRestaurant ? (
        <LevelLineChart
          weeks={weeks}
          levels={levels}
          lastCompleteIndex={lastCompleteIndex}
          summaryLevel={summaryLevel}
        />
      ) : (
        <LevelStackedBars
          weeks={weeks}
          levels={levels}
          lastCompleteIndex={lastCompleteIndex}
        />
      )}

      <LevelLegend levels={levels} activeRank={activeRank} />
    </div>
  );
};

const formatDaypartHour = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return `${String(Number(value) || 0).padStart(2, '0')}:00`;
};

const formatDaypartLabel = (daypart) => {
  if (!daypart || daypart.key === 'unmatched') {
    return 'Sin daypart';
  }
  const start = Number(daypart.hora_inicio);
  const end = Number(daypart.hora_fin);
  const suffix = end <= start ? ' (+1 día)' : '';
  return `${formatDaypartHour(start)} - ${formatDaypartHour(end)}${suffix}`;
};

const daypartColor = (daypart, index) => {
  if (daypart?.key === 'unmatched') {
    return UNMATCHED_DAYPART_COLOR;
  }
  return DAYPART_COLORS[index % DAYPART_COLORS.length];
};

const daypartLabelColor = (daypart, index) => {
  const color = daypartColor(daypart, index).toLowerCase();
  return ['#f2c94c', '#f2994a', '#9ca3af'].includes(color) ? '#2D2410' : '#FFFFFF';
};

const DaypartWeeklyBars = ({ data }) => {
  const dayparts = Array.isArray(data?.dayparts) ? data.dayparts : [];
  const weeks = Array.isArray(data?.weeks) ? data.weeks : [];
  const configuredDayparts = dayparts.filter((daypart) => daypart.key !== 'unmatched');
  const maxTotal = Math.max(...weeks.map((week) => Number(week.total) || 0), 0);

  if (!configuredDayparts.length) {
    return (
      <div className="daypart-weekly daypart-weekly--empty">
        <span className="section-eyebrow">Pronósticos por daypart</span>
        <p>Configura dayparts activos para ver la distribución semanal.</p>
      </div>
    );
  }

  if (!weeks.length || maxTotal <= 0) {
    return (
      <div className="daypart-weekly daypart-weekly--empty">
        <span className="section-eyebrow">Pronósticos por daypart</span>
        <p>Aún no hay pronósticos registrados para graficar en estas semanas.</p>
      </div>
    );
  }

  const VB_W = 900;
  const VB_H = 320;
  const PAD_L = 58;
  const PAD_R = 24;
  const PAD_T = 36;
  const PAD_B = 48;
  const innerW = VB_W - PAD_L - PAD_R;
  const innerH = VB_H - PAD_T - PAD_B;
  const totalBars = weeks.length || 1;
  const barGap = 14;
  const barW = Math.max(22, (innerW - barGap * (totalBars - 1)) / totalBars);
  const orderedDayparts = dayparts;

  return (
    <div className="daypart-weekly">
      <header className="daypart-weekly__header">
        <div>
          <span className="section-eyebrow">Pronósticos por daypart</span>
          <h4 className="daypart-weekly__title">Distribución semanal por rango horario</h4>
        </div>
        <div className="daypart-weekly__actions">
          <span className="daypart-weekly__meta">{weeks.length} semanas</span>
          <button
            className="gamification-export-btn"
            type="button"
            onClick={() => downloadDaypartSupport(data)}
          >
            <i data-lucide="download" />
            <span>Excel</span>
          </button>
        </div>
      </header>

      <div className="daypart-weekly__chart-frame">
        <svg
          className="daypart-weekly-chart"
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          role="img"
          aria-label="Pronósticos semanales por daypart"
          preserveAspectRatio="xMidYMid meet"
        >
          {[0, 0.5, 1].map((frac) => {
            const y = PAD_T + innerH * (1 - frac);
            return (
              <g key={`daypart-grid-${frac}`}>
                <line x1={PAD_L} x2={VB_W - PAD_R} y1={y} y2={y} className="daypart-weekly-chart__gridline" />
                <text x={PAD_L - 12} y={y + 4} textAnchor="end" className="daypart-weekly-chart__ytick">
                  {Math.round(maxTotal * frac)}
                </text>
              </g>
            );
          })}

          {weeks.map((week, weekIndex) => {
            const baseX = PAD_L + weekIndex * (barW + barGap);
            const total = Number(week.total) || 0;
            let cursorY = PAD_T + innerH;
            const segmentsByKey = (week.segments || []).reduce((acc, segment) => {
              acc[segment.key] = segment;
              return acc;
            }, {});

            return (
              <g key={week.week_start}>
                {!total ? (
                  <rect
                    x={baseX}
                    y={PAD_T + innerH - 4}
                    width={barW}
                    height={4}
                    className="daypart-weekly-chart__empty"
                    rx="2"
                  />
                ) : null}

                {orderedDayparts.map((daypart, daypartIndex) => {
                  const segment = segmentsByKey[daypart.key];
                  const count = Number(segment?.count) || 0;
                  if (!count || !total) {
                    return null;
                  }
                  const segmentHeight = (count / maxTotal) * innerH;
                  const y = cursorY - segmentHeight;
                  cursorY = y;
                  const percentage = Number(segment?.percentage || ((count / total) * 100));
                  const color = daypartColor(daypart, daypartIndex);
                  const label = `${count}/${Math.round(percentage)}%`;
                  const showSegmentLabel = segmentHeight >= 18 && barW >= 34;
                  return (
                    <g
                      key={`${week.week_start}-${daypart.key}`}
                    >
                      <title>
                        {`${daypart.nombre} · ${formatRangeLabel(week.week_start, week.week_end)} · ${count} pronósticos · ${percentage.toFixed(1)}%`}
                      </title>
                      <rect
                        x={baseX}
                        y={y}
                        width={barW}
                        height={Math.max(segmentHeight, 1)}
                        fill={color}
                        className="daypart-weekly-chart__segment"
                        rx={cursorY === y ? 5 : 0}
                        style={{ animationDelay: `${weekIndex * 50 + daypartIndex * 30}ms` }}
                      />
                      {showSegmentLabel ? (
                        <text
                          x={baseX + barW / 2}
                          y={y + segmentHeight / 2 + 3}
                          textAnchor="middle"
                          className="daypart-weekly-chart__segment-label"
                          fill={daypartLabelColor(daypart, daypartIndex)}
                        >
                          {label}
                        </text>
                      ) : null}
                    </g>
                  );
                })}

                <text x={baseX + barW / 2} y={PAD_T - 10} textAnchor="middle" className="daypart-weekly-chart__total">
                  {total}
                </text>
                <text
                  x={baseX + barW / 2}
                  y={VB_H - 20}
                  textAnchor={weekIndex === 0 ? 'start' : weekIndex === weeks.length - 1 ? 'end' : 'middle'}
                  className="daypart-weekly-chart__xtick"
                >
                  {formatWeekTick(week.week_start)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="daypart-weekly__legend">
        {orderedDayparts.map((daypart, index) => (
          <span className="daypart-weekly__legend-item" key={daypart.key}>
            <span className="daypart-weekly__legend-dot" style={{ background: daypartColor(daypart, index) }} />
            <span>{daypart.nombre}</span>
            <em>{formatDaypartLabel(daypart)}</em>
          </span>
        ))}
      </div>
    </div>
  );
};

const GamificationDashboard = ({
  authenticated,
  restaurantItems
}) => {
  const {
    filters,
    summary,
    rankings,
    levels,
    latestSync,
    restaurantCount,
    weeks,
    currentWeek,
    daypartWeekly,
    loading,
    error,
    setFilterValue,
    reload
  } = useGamification({ authenticated });

  const showRankings = restaurantCount > 1;
  const isSingleRestaurant = restaurantCount === 1 || Boolean(filters.restaurantName);

  // The hero card is labeled "NIVEL · ÚLTIMA SEMANA COMPLETA", so it must
  // mirror the last entry in `weeks` (the chart pin), NOT the multi-week
  // aggregate `summary`. Falling back to `summary` only if `weeks` is empty.
  const lastWeekEntry = Array.isArray(weeks) && weeks.length ? weeks[weeks.length - 1] : null;
  const lastWeekScore = lastWeekEntry?.score || null;
  const heroSource = lastWeekScore || summary;
  const level = heroSource?.level || EmptyLevel();
  const progress = Number.isFinite(Number(heroSource?.progress_pct)) ? Number(heroSource.progress_pct) : 0;
  const measurementText = useMemo(() => {
    if (!heroSource) {
      return 'Selecciona un rango para iluminar el desempeño.';
    }
    if (!heroSource.days_effective && heroSource.days_na) {
      return `${heroSource.days_na} días en ventana de gracia; aún no penalizan.`;
    }
    if (!heroSource.days_effective) {
      return 'Sin días medidos esta semana.';
    }
    return `${heroSource.days_with_forecast} de ${heroSource.days_effective} días operados con pronóstico en la última semana completa.`;
  }, [heroSource]);
  const rangeLabel = formatRangeLabel(filters.startDate, filters.endDate);

  useLucideIcons([
    loading,
    error,
    summary?.level_slug,
    rankings.top?.length,
    rankings.bottom?.length,
    showRankings,
    weeks?.length,
    daypartWeekly?.weeks?.length,
    currentWeek?.days?.length
  ]);

  if (!authenticated) {
    return null;
  }

  if (error && !summary) {
    return (
      <section className="gamification-dashboard gamification-dashboard-fallback" aria-label="Gamificación Hat Trick">
        <div className="gamification-fallback-content">
          <div className="gamification-fallback-icon">
            <i data-lucide="alert-circle" />
          </div>
          <div>
            <strong>Gamificación no disponible</strong>
            <span>Los pronósticos siguen funcionando. Reintenta o revisa la sincronización.</span>
          </div>
        </div>
        <button className="gamification-refresh compact" type="button" onClick={reload} disabled={loading}>
          <i data-lucide={loading ? 'loader' : 'refresh-cw'} />
          <span>{loading ? 'Cargando' : 'Reintentar'}</span>
        </button>
      </section>
    );
  }

  return (
    <section className={`gamification-dashboard ${loading ? 'is-loading' : ''}`} aria-label="Gamificación Hat Trick" aria-busy={loading}>
      <div className="gamification-headline">
        <div className="gamification-headline__copy">
          <span className="section-eyebrow">Hat Trick · Tablero</span>
          <h2>Tu temporada en una mirada.</h2>
          <p>Las últimas <strong>8 semanas</strong> de juego, con la jornada actual en vivo.</p>
        </div>
        <div className="gamification-headline__meta">
          <span className="gamification-sync-pill">
            <i data-lucide="radio" />
            <span>{formatSyncDate(latestSync?.fetched_at)}</span>
          </span>
          <button className="gamification-refresh" type="button" onClick={reload} disabled={loading}>
            <i data-lucide={loading ? 'loader' : 'refresh-cw'} />
            <span>{loading ? 'Cargando' : 'Actualizar'}</span>
          </button>
        </div>
      </div>

      <div className="gamification-controls">
        <div className="gamification-filters">
          <label>
            <span>Inicio</span>
            <input
              type="date"
              value={filters.startDate}
              onChange={(event) => setFilterValue('startDate', event.target.value)}
            />
          </label>
          <label>
            <span>Fin</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(event) => setFilterValue('endDate', event.target.value)}
            />
          </label>
          {showRankings ? (
            <label>
              <span>Restaurante</span>
              <select
                value={filters.restaurantName}
                onChange={(event) => setFilterValue('restaurantName', event.target.value)}
              >
                <option value="">Todos</option>
                {(restaurantItems || []).map((restaurant) => (
                  <option key={restaurant.value} value={restaurant.value}>{restaurant.label}</option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        <span className="gamification-range-pill">{rangeLabel || '8 semanas'}</span>
      </div>

      {error ? <div className="gamification-error">{error}</div> : null}

      <div className="gamification-hero gamification-hero--with-chart">
        <div className={`gamification-hero__topline ${!isSingleRestaurant ? 'gamification-hero__topline--no-logo' : ''}`}>
          {isSingleRestaurant ? (
            <LevelLogo level={level} size={184} className="gamification-hero__logo" />
          ) : null}
          <div className="gamification-hero__copy">
            <span className="section-eyebrow">
              {isSingleRestaurant ? 'Nivel · última semana completa' : 'Resumen agregado · última semana completa'}
            </span>
            {isSingleRestaurant ? (
              <>
                <h3>{level.nombre}</h3>
                <p>{level.descripcion}</p>
              </>
            ) : (
              <h3 className="gamification-hero__aggregate-title">Desempeño consolidado de la red</h3>
            )}
            <div className="gamification-hero__metrics">
              <div className="hero-metric">
                <strong>{Math.round(progress)}<span>%</span></strong>
                <span>Cumplimiento</span>
              </div>
              <div className="hero-metric">
                <strong>{heroSource?.days_hat_trick || 0}</strong>
                <span>Hat Tricks</span>
              </div>
              <div className="hero-metric">
                <strong>{heroSource?.days_with_forecast || 0}<span>/{heroSource?.days_effective || 0}</span></strong>
                <span>Días pronosticados</span>
              </div>
              <div className="hero-metric">
                <strong>{heroSource?.forecast_events || 0}</strong>
                <span>Pronósticos</span>
              </div>
            </div>
            <p className="gamification-hero__note">{measurementText}</p>
          </div>
        </div>

        <LevelTrajectory
          weeks={weeks}
          levels={levels}
          isSingleRestaurant={isSingleRestaurant}
          summaryLevel={level}
        />
        <DaypartWeeklyBars data={daypartWeekly} />
      </div>

      <WeekTrack data={currentWeek} />

      {!isSingleRestaurant ? (
        <div className="gamification-rankings">
          <RankingList title="Mejores" icon="trophy" items={rankings.top || []} />
          <RankingList title="Por atender" icon="activity" items={rankings.bottom || []} />
        </div>
      ) : null}
    </section>
  );
};

export default GamificationDashboard;
